import {
  ADMIN_INITIALS,
  LANG,
  MANAGER_PROFILE_INITIALS,
  MANAGER_PROFILES,
  TAG_TYPES,
  QUERY_STATUS,
  STATUS_MISSION,
} from "constants/constants";
import CategoryTag from "core/tags/category-tag/CategoryTag";
import moment from "moment";
import { FormattedMessage } from "react-intl";
import { Link } from "react-router-dom";
import { getFormattedDate } from "./functions";
import { getEntityIsAllowed } from "./helper";
import { calcDuration } from "./toolsManagerHelper";
import { format } from "date-fns";

export const getPersonFirstLastName = ({ list, id, type }) =>
  list
    ?.filter((elm) => elm?.id === id)
    .map((elm) => ({
      label: elm.full_name || `${elm.first_name} ${elm.last_name}`,
      value: elm?.id,
      type: type || TAG_TYPES.PEOPLE,
      profile: elm?.profile_id,
    }));
export const getClientInfo = ({ list, id }) =>
  list
    ?.filter((elm) => elm.id === id)
    .map((elm) => ({
      label: elm?.full_name || elm?.name,
      value: elm?.id,
      tag: TAG_TYPES.CLIENTS,
    }));

// Accept both "." and "," as decimal separator before numeric parsing.
export const normalizeDecimalSeparator = (value) =>
  typeof value === "string" ? value.replace(",", ".") : value;

// Two fixed decimals, no grouping so normalizeDecimalSeparator can parse it back.
export const formatDecimalForInput = (value, lang) => {
  if (value === null || value === undefined || value === "") return value;
  const number = Number(normalizeDecimalSeparator(value));
  if (!Number.isFinite(number)) return value;
  const fixed = number.toFixed(2);
  return lang === LANG.FR ? fixed.replace(".", ",") : fixed;
};

export const salaryFormatter = (salary, includeEuro = true) => {
  const salaryString = salary.toFixed(2).toString();
  const [integerPart, decimalPart] = salaryString.split(".");

  let formattedIntegerPart = "";
  for (let i = integerPart.length - 1, j = 1; i >= 0; i--, j++) {
    if (j % 3 === 0 && i !== 0) {
      formattedIntegerPart = "," + integerPart[i] + formattedIntegerPart;
    } else {
      formattedIntegerPart = integerPart[i] + formattedIntegerPart;
    }
  }

  const formattedSalary = formattedIntegerPart + "." + decimalPart;
  if (includeEuro) {
    return formattedSalary + " €";
  } else {
    return formattedSalary;
  }
};

export const formatDate = (date) => {
  if (!date) return "";
  const dateToFormat = moment(date);
  const format = "DD/MM/YYYY";
  return dateToFormat.format(format);
};

export const formatDateToYYYYMMDD = (date) => {
  if (!date) return "";
  const dateToFormat = moment(date);
  const format = "YYYY-MM-DD";
  return dateToFormat.format(format);
};

// The category of a line follows from its job (redmine 12940): the mission no
// longer stores it. The referential holds a dozen categories, each carrying its
// job ids, so the lookup is negligible and saves threading one more map down to
// here.
const findJobCategoryId = (jobCategoryList, jobId) =>
  jobId
    ? jobCategoryList?.find(
        (category) => !category?.is_protected && category?.jobs?.includes(jobId)
      )?.id
    : null;

export const getFormattedMission = ({
  handleDownloadMissionPPT,
  list = [],
  propsObject = null,
  workersList = [],
  clientsList = [],
}) => {
  const {
    getClientSlug,
    getWorkerSlug,
    jobList,
    jobCategoryList,
    getWorkerProfile,
    getJobLabel,
    lang,
    getStatusLabel,
    userScope,
    openMissionModal,
    getEntityName,
    namesByFilters,
    creationFileStatus,
    setIsAllSelected,
    setSelectedMissionIds,
    isAllSelected,
    selectedMissionIds,
  } = propsObject;

  const toggleMissionId = (missionId) => {
    setIsAllSelected(false);
    setSelectedMissionIds((prev) =>
      prev.includes(missionId)
        ? prev.filter((e) => e !== missionId)
        : [...prev, missionId]
    );
  };

  const isMissionChecked = (missionId) =>
    isAllSelected || selectedMissionIds.includes(missionId);

  return list?.map((elm) => {
    const {
      client_id,
      worker_id,
      name,
      slug,
      domain_id,
      current_fee,
      status,
      id,
      start_date,
      end_date,
    } = elm || {};

    const today = format(new Date(), "yyyy-MM-dd");
    const clientEntity = namesByFilters?.find((item) => item.id === client_id);
    const isClientDeleted = clientEntity?.deleted;
    const isClientAllowed = getEntityIsAllowed(client_id, namesByFilters);
    const clientSlug =
      !isClientDeleted &&
      isClientAllowed !== false &&
      getClientSlug(client_id, clientsList);

    const clientName = getEntityName(client_id);

    const workerProfile = getWorkerProfile({
      workers: workersList,
      id: worker_id,
    });

    const workerSlug = getWorkerSlug(worker_id, workersList);
    const isWorkerAllowed = getEntityIsAllowed(worker_id, namesByFilters);

    const workerName = getEntityName(worker_id);

    return {
      select: (
        <div className="checkbox-wrapper">
          <input
            className="checkbox"
            type="checkbox"
            checked={isMissionChecked(id)}
            onChange={() => toggleMissionId(id)}
          />
        </div>
      ),
      name: (
        <Link
          to={`/${userScope}/missions/${slug}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {name}
        </Link>
      ),
      client_id: clientSlug ? (
        <Link
          to={`/${userScope}/client/${clientSlug}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {clientName}
        </Link>
      ) : (
        clientName
      ),
      consultant:
        isWorkerAllowed === false ? (
          <span>{workerName}</span>
        ) : (
          <Link
            to={`/${userScope}/${
              workerProfile === "cslt" ? "consultant" : "subcontractor"
            }/${workerSlug}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {workerName}
          </Link>
        ),
      job: (
        <>
          {findJobCategoryId(jobCategoryList, elm?.domain_id) && (
            <CategoryTag
              type={TAG_TYPES.FUNCTIONS}
              label={getJobLabel(
                jobCategoryList,
                findJobCategoryId(jobCategoryList, elm.domain_id),
                lang
              )}
            />
          )}
          {elm?.domain_id && (
            <CategoryTag
              type={TAG_TYPES.FUNCTIONS}
              label={getJobLabel(jobList, domain_id, lang)}
            />
          )}
        </>
      ),
      salary: current_fee ? salaryFormatter(current_fee, false) : "-",
      duration: (
        <>
          <p>
            {calcDuration({
              start: start_date,
              end: end_date,
              status,
              lang,
            })}
          </p>
          <p>{`${getFormattedDate(start_date, lang)} - ${
            status === STATUS_MISSION.ONE
              ? getFormattedDate(today, lang)
              : getFormattedDate(end_date, lang)
          }`}</p>
        </>
      ),
      status: (
        <div className="status_column">
          {getStatusLabel(status)}
          <div className="links">
            <button
              className="div-link manager-tools button-style"
              onClick={() => openMissionModal(elm)}
            >
              <FormattedMessage
                id="desc_mission"
                defaultMessage={"Descriptif mission"}
              />
            </button>
            <button
              onClick={() => {
                if (creationFileStatus === QUERY_STATUS.LOADING) return;
                handleDownloadMissionPPT({ id, slug });
              }}
              className="div-link"
              disabled={creationFileStatus === QUERY_STATUS.LOADING}
            >
              <FormattedMessage
                id="export_sheet"
                defaultMessage={"Exporter Ref"}
              />
            </button>
          </div>
        </div>
      ),
    };
  });
};
const idFormatted = (id) => id?.toString().padStart(8, "0");
const branchIdFormatted = (branch_id) => branch_id?.toString().padStart(2, "0");
const profileIdFormatted = (profile_id) => {
  return MANAGER_PROFILES?.some((profile) => profile === profile_id)
    ? MANAGER_PROFILE_INITIALS
    : ADMIN_INITIALS;
};

export const formatConsultant = ({ id }) => id && idFormatted(id);
export const formatStaff = ({ branch_id, profile_id, id }) =>
  branch_id &&
  profile_id &&
  id &&
  `${branchIdFormatted(branch_id)}${profileIdFormatted(
    profile_id
  )}${idFormatted(id)}`;
export const formatSubcontractor = ({ subcontractorCompany, id }) =>
  id &&
  `${
    subcontractorCompany?.company_name
      ? subcontractorCompany.company_name + " - "
      : ""
  }${idFormatted(id)}`;
export const formatClient = ({ analytical_code, sigle }) =>
  analytical_code || sigle || "";
export const formatSubcontractingCompany = ({ siren }) => siren || "";
export const formatBillingClient = ({ siren }) => siren || "";
export const formatMission = ({ analytical_code }) => analytical_code || "";

export const handleInfoPopupContent = ({
  missionId,
  errorCellName,
  errorCellNameFormatted,
  apiErrorResponse,
  lang,
}) => {
  const errorInfo = errorCellNameFormatted?.[errorCellName];
  const { messageId, messageDefault } = errorInfo?.errorMessage || {};
  const errorCode = errorInfo?.code;

  const errors = apiErrorResponse?.find(
    (e) => e?.missionId === missionId
  )?.errorsDetail;
  const cellError = errors?.find((e) => e?.code === errorCode);

  const formattedDate = getFormattedDate(cellError?.message, lang);

  return (
    <>
      <FormattedMessage id={messageId} defaultMessage={messageDefault} />{" "}
      {formattedDate}.
    </>
  );
};
